import React, { useState, useEffect } from 'react';
import { X, Clock, Settings, Calendar, ToggleLeft, ToggleRight } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../slices';
import { updateMarket, patchMarket, clearError, clearSuccess } from '../../slices/market/marketSlice';
import { Market as MarketType } from '../../slices/market/marketSlice';

interface EditMarketModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  market: MarketType | null;
}

const EditMarketModal: React.FC<EditMarketModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  market,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { loading, error, success } = useSelector((state: RootState) => state.Market);
  
  const [formData, setFormData] = useState({
    name: '',
    api_key_name: '',
    open_time: '',
    close_time: '',
    open_result_time: '',
    close_result_time: '',
    is_active: true,
    saturday_open: true,
    sunday_open: true,
    auto_result: false,
    previous_day_check: true,
    open_pana: '',
    open_digit: '',
    close_pana: '',
    close_digit: '',
    jodi_digit: ''
  });

  const [activeTab, setActiveTab] = useState<'general' | 'timing' | 'weekend' | 'results'>('general');
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isUpdatingManualClosure, setIsUpdatingManualClosure] = useState(false);
  const [manualClosureError, setManualClosureError] = useState<string | null>(null);

  useEffect(() => {
    if (market && isOpen) {
      setIsLoadingData(true);
      console.log('Loading market data for edit:', market);
      
      // Simulate a small delay to show loading state
      setTimeout(() => {
        setFormData({
          name: market.name || '',
          api_key_name: '', // This field might not be in the API response
          open_time: market.open_time || '',
          close_time: market.close_time || '',
          open_result_time: market.open_result_time || '',
          close_result_time: market.close_result_time || '',
          is_active: market.is_active ?? true,
          saturday_open: market.saturday_open || false,
          sunday_open: market.sunday_open || false,
          auto_result: market.auto_results || false,
          previous_day_check: true, // This field might not be in the API response
          open_pana: market.open_pana || '',
          open_digit: market.open_digit || '',
          close_pana: market.close_pana || '',
          close_digit: market.close_digit || '',
          jodi_digit: market.jodi_digit || ''
        });
        setIsLoadingData(false);
      }, 100);
    }
  }, [market, isOpen]);

  // Auto-dismiss success/error messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        dispatch(clearSuccess());
        onSuccess?.();
        onClose();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [success, dispatch, onClose, onSuccess]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const getMarketLocalDate = () => {
    const timeZone = market?.timezone || 'Asia/Kolkata';

    try {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).formatToParts(new Date());

      const year = parts.find(part => part.type === 'year')?.value;
      const month = parts.find(part => part.type === 'month')?.value;
      const day = parts.find(part => part.type === 'day')?.value;

      if (!year || !month || !day) {
        throw new Error('Unable to determine market-local date');
      }

      return `${year}-${month}-${day}`;
    } catch (dateError) {
      console.error('Unable to determine market-local date:', dateError);
      return null;
    }
  };

  const handleManualClosureChange = async () => {
    if (!market || isUpdatingManualClosure) return;

    const isClosedForToday = Boolean(market.manual_closed_date);
    const marketLocalDate = getMarketLocalDate();

    if (!isClosedForToday && !marketLocalDate) {
      setManualClosureError('Unable to determine the market-local date. No change was made.');
      return;
    }

    setManualClosureError(null);
    setIsUpdatingManualClosure(true);

    try {
      await dispatch(patchMarket({
        id: market.id,
        manual_closed_date: isClosedForToday ? null : marketLocalDate,
      })).unwrap();

      onSuccess?.();
      onClose();
    } catch (actionError: any) {
      console.error('Unable to update manual market closure:', actionError);
      setManualClosureError(
        typeof actionError === 'string'
          ? actionError
          : 'Unable to update market closure. Please try again.'
      );
    } finally {
      setIsUpdatingManualClosure(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!market) return;

    // Prepare the data for API
    const updateData = {
      id: market.id,
      name: formData.name,
      open_time: formData.open_time,
      close_time: formData.close_time,
      open_result_time: formData.open_result_time,
      close_result_time: formData.close_result_time,
      saturday_open: formData.saturday_open,
      sunday_open: formData.sunday_open,
      auto_results: formData.auto_result
    };

    dispatch(updateMarket(updateData));
  };

  const handleTabChange = (tab: 'general' | 'timing' | 'weekend' | 'results') => {
    setActiveTab(tab);
  };

  const ToggleSwitch = ({ 
    name, 
    checked, 
    onChange, 
    leftLabel, 
    rightLabel 
  }: { 
    name: string; 
    checked: boolean; 
    onChange: (checked: boolean) => void; 
    leftLabel: string; 
    rightLabel: string; 
  }) => (
    <div className="flex items-center space-x-3">
      <span className={`text-sm font-medium ${!checked ? 'text-blue-600' : 'text-gray-500'}`}>
        {leftLabel}
      </span>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
          checked ? 'bg-blue-600' : 'bg-gray-200'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
      <span className={`text-sm font-medium ${checked ? 'text-blue-600' : 'text-gray-500'}`}>
        {rightLabel}
      </span>
    </div>
  );

  if (!isOpen || !market) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden relative mx-2 sm:mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Update General Market</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-4 sm:space-x-8 px-4 sm:px-6 overflow-x-auto">
            {[
              { id: 'general', label: 'General' },
              { id: 'timing', label: 'Timing' },
              { id: 'weekend', label: 'Weekend' },
              { id: 'results', label: 'Results' }
            ].map(({ id, label }) => (
              <button
                key={id}
                onClick={() => handleTabChange(id as any)}
                className={`py-3 px-1 border-b-2 font-medium text-sm ${
                  activeTab === id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {label}
              </button>
            ))}
          </nav>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 overflow-y-auto max-h-[60vh] sm:max-h-[60vh]">
          {isLoadingData && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <span className="ml-2 text-gray-600">Loading market details...</span>
            </div>
          )}
          
          {!isLoadingData && (
            <>
            {activeTab === 'general' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Api Key Name
                </label>
                <input
                  type="text"
                  name="api_key_name"
                  value={formData.api_key_name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Market Status
                </label>

                <div className={`rounded-lg border p-4 ${
                  market.manual_closed_date
                    ? 'border-red-200 bg-red-50'
                    : 'border-green-200 bg-green-50'
                }`}>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <p className={`text-sm font-semibold ${
                        market.manual_closed_date
                          ? 'text-red-800'
                          : 'text-green-800'
                      }`}>
                        {market.manual_closed_date
                          ? 'Closed for Today'
                          : 'Normal Schedule Active'}
                      </p>

                      <p className="mt-1 text-xs text-gray-600">
                        {market.manual_closed_date
                          ? `Betting is manually closed for ${market.manual_closed_date}. It automatically expires after the market-local day.`
                          : 'The market follows its normal configured timing and result rules.'}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={handleManualClosureChange}
                      disabled={loading || isLoadingData || isUpdatingManualClosure}
                      className={`w-full sm:w-auto px-4 py-2 text-sm font-semibold text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                        market.manual_closed_date
                          ? 'bg-green-600 hover:bg-green-700'
                          : 'bg-red-600 hover:bg-red-700'
                      }`}
                    >
                      {isUpdatingManualClosure
                        ? 'Updating...'
                        : market.manual_closed_date
                          ? 'Reopen Today'
                          : 'Close for Today'}
                    </button>
                  </div>

                  {manualClosureError && (
                    <div className="mt-3 text-sm text-red-700">
                      {manualClosureError}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'timing' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Open Time
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="time"
                    name="open_time"
                    value={formData.open_time}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Close Time
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="time"
                    name="close_time"
                    value={formData.close_time}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Open Result Time
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="time"
                    name="open_result_time"
                    value={formData.open_result_time}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Close Result Time
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="time"
                    name="close_result_time"
                    value={formData.close_result_time}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'weekend' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Saturday Open
                </label>
                <ToggleSwitch
                  name="saturday_open"
                  checked={formData.saturday_open}
                  onChange={(checked) => setFormData(prev => ({ ...prev, saturday_open: checked }))}
                  leftLabel="Enable"
                  rightLabel="Disable"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sunday Open
                </label>
                <ToggleSwitch
                  name="sunday_open"
                  checked={formData.sunday_open}
                  onChange={(checked) => setFormData(prev => ({ ...prev, sunday_open: checked }))}
                  leftLabel="Enable"
                  rightLabel="Disable"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Auto Result
                </label>
                <ToggleSwitch
                  name="auto_result"
                  checked={formData.auto_result}
                  onChange={(checked) => setFormData(prev => ({ ...prev, auto_result: checked }))}
                  leftLabel="Enable"
                  rightLabel="Disable"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Previous Day Check
                </label>
                <ToggleSwitch
                  name="previous_day_check"
                  checked={formData.previous_day_check}
                  onChange={(checked) => setFormData(prev => ({ ...prev, previous_day_check: checked }))}
                  leftLabel="Enable"
                  rightLabel="Disable"
                />
              </div>
            </div>
          )}

          {activeTab === 'results' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Open Pana
                </label>
                <input
                  type="text"
                  name="open_pana"
                  value={formData.open_pana}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., 123"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Open Digit
                </label>
                <input
                  type="text"
                  name="open_digit"
                  value={formData.open_digit}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., 4"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Close Pana
                </label>
                <input
                  type="text"
                  name="close_pana"
                  value={formData.close_pana}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., 456"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Close Digit
                </label>
                <input
                  type="text"
                  name="close_digit"
                  value={formData.close_digit}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., 7"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Jodi Digit
                </label>
                <input
                  type="text"
                  name="jodi_digit"
                  value={formData.jodi_digit}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., 47"
                />
              </div>
            </div>
          )}
          </>
          )}

          {/* Error/Success Messages */}
          {error && (
            <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {success && (
            <div className="mt-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
              Market updated successfully!
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || isLoadingData}
              className="w-full sm:w-auto px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Updating...' : 'Update Market'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditMarketModal;
